"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { FunscriptAction } from "@/lib/script-store"
import { DRAFT_LIMITS } from "@/lib/draft-limits"

// ── Types ─────────────────────────────────────────────────────────────────────

export type DraftEditorType = "manual" | "curve" | "block" | "vertical" | "story-scripter" | "pal-scripter"

export interface ScriptDraft {
  id: string
  name: string
  description: string
  tags: string[]
  actions: FunscriptAction[]
  contentUrl?: string
  contentType?: string
  editorType: DraftEditorType
  editorState?: any
  thumbnailUrl?: string
  createdAt: number
  updatedAt: number
}

export { DRAFT_LIMITS }

// ── Store ─────────────────────────────────────────────────────────────────────

interface DraftState {
  drafts: ScriptDraft[]
  activeDraftId: string | null
  isDirty: boolean

  // CRUD
  createDraft: (data: Partial<ScriptDraft>) => ScriptDraft | null
  updateDraft: (id: string, data: Partial<ScriptDraft>) => void
  deleteDraft: (id: string) => void
  setActiveDraft: (id: string | null) => void
  setDirty: (dirty: boolean) => void
  getDraft: (id: string) => ScriptDraft | undefined

  // Limit helpers
  getDraftLimit: (isPremium: boolean, isGuest: boolean) => number
  canCreateDraft: (isPremium: boolean, isGuest: boolean) => boolean

  // Server sync
  syncFromServer: () => Promise<void>
  upsertToServer: (draft: ScriptDraft) => Promise<void>
  deleteFromServer: (id: string) => Promise<void>

  // Guest → login migration: pushes all local drafts to DB then re-syncs
  migrateGuestDrafts: () => Promise<void>
}

function makeId() {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export const useDraftStore = create<DraftState>()(
  persist(
    (set, get) => ({
      drafts: [],
      activeDraftId: null,
      isDirty: false,

      getDraft: (id) => get().drafts.find((d) => d.id === id),

      getDraftLimit: (isPremium, isGuest) => {
        if (isGuest) return DRAFT_LIMITS.guest
        if (isPremium) return DRAFT_LIMITS.paid
        return DRAFT_LIMITS.free
      },

      canCreateDraft: (isPremium, isGuest) => {
        const limit = get().getDraftLimit(isPremium, isGuest)
        return get().drafts.length < limit
      },

      createDraft: (data) => {
        const now = Date.now()
        const draft: ScriptDraft = {
          id: makeId(),
          name: data.name ?? "Untitled Draft",
          description: data.description ?? "",
          tags: data.tags ?? [],
          actions: data.actions ?? [],
          contentUrl: data.contentUrl,
          contentType: data.contentType,
          editorType: data.editorType ?? "manual",
          editorState: data.editorState,
          thumbnailUrl: data.thumbnailUrl,
          createdAt: now,
          updatedAt: now,
        }
        set((s) => ({ drafts: [draft, ...s.drafts], activeDraftId: draft.id, isDirty: false }))
        return draft
      },

      updateDraft: (id, data) => {
        set((s) => ({
          drafts: s.drafts.map((d) =>
            d.id === id ? { ...d, ...data, id, updatedAt: Date.now() } : d
          ),
          isDirty: false,
        }))
      },

      deleteDraft: (id) => {
        set((s) => ({
          drafts: s.drafts.filter((d) => d.id !== id),
          activeDraftId: s.activeDraftId === id ? null : s.activeDraftId,
        }))
      },

      setActiveDraft: (id) => set({ activeDraftId: id, isDirty: false }),

      setDirty: (dirty) => set({ isDirty: dirty }),

      // ── Server sync ──────────────────────────────────────────────────────────

      syncFromServer: async () => {
        try {
          const res = await fetch("/api/drafts")
          if (!res.ok) return
          const { drafts: serverDrafts } = await res.json() as { drafts: ScriptDraft[] }
          if (!Array.isArray(serverDrafts)) return
          // Merge: server is authoritative for logged-in users; keep any local-only drafts that
          // don't have a server counterpart (identified by id not present on server).
          const serverIds = new Set(serverDrafts.map((d) => d.id))
          const localOnly = get().drafts.filter((d) => !serverIds.has(d.id))
          set({ drafts: [...serverDrafts, ...localOnly] })
        } catch {
          // Non-critical; local cache is still valid
        }
      },

      upsertToServer: async (draft) => {
        try {
          await fetch("/api/drafts", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(draft),
          })
        } catch {
          // Silently ignore; draft is still saved locally
        }
      },

      deleteFromServer: async (id) => {
        try {
          await fetch(`/api/drafts?id=${encodeURIComponent(id)}`, { method: "DELETE" })
        } catch {}
      },

      migrateGuestDrafts: async () => {
        const { drafts, upsertToServer, syncFromServer } = get()
        if (!drafts.length) return
        // Push each local draft to server (ignore errors for individual drafts)
        await Promise.allSettled(drafts.map(upsertToServer))
        // Re-sync so server IDs are canonical
        await syncFromServer()
      },
    }),
    {
      name: "goonru-drafts",
      // Persist everything except sync methods
      partialize: (s) => ({ drafts: s.drafts, activeDraftId: s.activeDraftId }),
    },
  ),
)
